import { useMemo, useState } from 'react'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { EsitoAnalisi } from '@praticheenergia/ad1-engine'
import { DESCRIZIONE_CONTATORE } from '@praticheenergia/domain'
import { elencaContatori } from './contatori.api'
import { analizzaFile, confronta, importaContatori } from './import-xml.api'

const PERIODO: Record<string, string> = { annuale: 'annuale', semestre_1: 'del I semestre', semestre_2: 'del II semestre' }

/** ANAG-22: contatori dall'XML AD-1 dell'anno precedente. Prima la fase di
 * test (cosa arriva, cosa c'e' gia', avvisi), poi l'import dei soli nuovi. */
export function ImportaXml({ impiantoId, codiceDitta }: { impiantoId: number; codiceDitta: string }) {
  const queryClient = useQueryClient()
  const [aperto, setAperto] = useState(false)
  const [esito, setEsito] = useState<EsitoAnalisi | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [resoconto, setResoconto] = useState<{ importati: number; errori: string[] } | null>(null)

  const { data: esistenti = [] } = useQuery({
    queryKey: ['contatori', impiantoId],
    queryFn: () => elencaContatori(impiantoId),
    enabled: aperto,
  })

  const confronto = useMemo(
    () => (esito ? confronta(esito, codiceDitta, esistenti.map((c) => c.matricola)) : null),
    [esito, codiceDitta, esistenti],
  )
  const nuovi = confronto?.righe.filter((r) => r.nuovo).map((r) => r.contatore) ?? []

  function chiudi() {
    setAperto(false)
    setEsito(null)
    setErrore(null)
    setResoconto(null)
  }

  async function suFile(file: File) {
    setErrore(null)
    setEsito(null)
    setResoconto(null)
    try {
      setEsito(await analizzaFile(file))
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  async function suImporta() {
    setResoconto(await importaContatori(impiantoId, nuovi))
    // I contatori entrano in letture e stato delle dichiarazioni: si rinfresca tutto.
    await queryClient.invalidateQueries()
  }

  return (
    <>
      <Button variant="outlined" sx={{ mb: 2 }} onClick={() => setAperto(true)}>
        Importa dall XML dell anno precedente
      </Button>
      <Dialog open={aperto} onClose={chiudi} fullWidth maxWidth="md">
        <DialogTitle>Importa i contatori da una dichiarazione</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Carica l XML della dichiarazione AD-1 dell anno precedente, anche firmato (.xml.p7m). Prima vedi cosa
              verrebbe importato: non si scrive nulla finche non confermi.
            </Typography>
            <Button component="label" variant="outlined">
              Scegli il file
              <input
                hidden
                type="file"
                accept=".xml,.p7m"
                aria-label="File XML della dichiarazione"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void suFile(file)
                }}
              />
            </Button>

            {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
            {confronto?.blocco && <Alert severity="error" role="alert">{confronto.blocco}</Alert>}

            {esito && confronto && (
              <>
                <Typography>
                  Dichiarazione {PERIODO[esito.periodo ?? ''] ?? ''} {esito.annoImposta} dell impianto {esito.codiceDitta}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Matricola</TableCell>
                      <TableCell>Contatore</TableCell>
                      <TableCell>Tipologia di consumo</TableCell>
                      <TableCell>Esito</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {confronto.righe.map(({ contatore, nuovo }) => (
                      <TableRow key={contatore.matricola}>
                        <TableCell>{contatore.matricola}</TableCell>
                        <TableCell>{DESCRIZIONE_CONTATORE[contatore.codice]}</TableCell>
                        <TableCell>{contatore.tipologiaConsumo ?? ''}</TableCell>
                        <TableCell>{nuovo ? 'Da importare' : 'Gia presente'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {esito.diagnostiche
                  .filter((d) => d.gravita === 'avviso')
                  .map((d) => (
                    <Alert key={d.codice + d.messaggio} severity="warning" role="note">
                      {d.messaggio}
                    </Alert>
                  ))}
                {!confronto.blocco && nuovi.length === 0 && resoconto === null && (
                  <Typography>Tutti i contatori del file sono gia presenti</Typography>
                )}
              </>
            )}

            {resoconto && (
              <>
                <Alert severity={resoconto.errori.length ? 'warning' : 'success'} role="status">
                  {resoconto.importati} contatori importati
                </Alert>
                {resoconto.errori.map((e) => (
                  <Typography key={e} color="error">{e}</Typography>
                ))}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={chiudi}>{resoconto ? 'Chiudi' : 'Annulla'}</Button>
          {confronto && !confronto.blocco && nuovi.length > 0 && resoconto === null && (
            <Button variant="contained" onClick={() => void suImporta()}>
              Importa {nuovi.length} contatori
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
